import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import {
  getMarginOverride,
  updatePricingStrategy,
  updatePricing,
} from "../../../../services/vendor/vendor.service";
import UpdateButton from "./UpdateButton";

const ManageMarginOverride = ({ selectedItem, isRender, setIsRender }) => {
  const navigate = useNavigate(); // Initialize navigate hook

  // State for loading indicators
  const [isUpdating, setIsUpdating] = useState(false); // For the update operation
  const [isFetchingData, setIsFetchingData] = useState(true); // For initial data fetch

  // State to hold the array of category override data
  const [categoryOverrideData, setCategoryOverrideData] = useState([]);

  // console.log("Selected Item:", selectedItem);
  // console.log("Category Override Data:", categoryOverrideData);
  const [loading, setLoading] = useState(false);
  // console.log(selectedItem);

  const [formData, setFormData] = useState({
    mapPercentage: selectedItem?.mapPercentage,
    nonMapPercentage: selectedItem?.nonMapPercentage,
  });

  const handleGblUpdate = async (id) => {
    setLoading(true);
    try {
      // First API call - updatePricing
      const pricingRes = await updatePricing({
        id,
        isCustomPricing: false,
        ...formData,
      });

      // Second API call - updatePricingStrategy
      const strategyRes = await updatePricingStrategy({
        id: selectedItem?._id,
        isCustomPricing: false,
        override: categoryOverrideData,
      });

      // Handle responses
      if (pricingRes?.data?.message) {
        console.log("Pricing update successful:", pricingRes.data.message);
      }

      if (strategyRes?.data?.message) {
        console.log("Strategy update successful:", strategyRes.data.message);
      }

      // Update state and trigger re-render
      setLoading(false);
      if (setIsRender) setIsRender(!isRender);

      console.log("Simulating update with data:", formData);
    } catch (error) {
      setLoading(false);
      console.error("Update failed:", error);
    }
  };

 const handleChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: value === "" ? "" : Number(value),
  }));
};

const handleGblIncrement = (name) => {
  setFormData((prev) => ({
    ...prev,
    [name]: (prev[name] || 0) + 1,
  }));
};

const handleGblDecrement = (name) => {
  setFormData((prev) => ({
    ...prev,
    [name]: (prev[name] || 0) - 1, // ✅ Now supports negative values
  }));
};

  // Function to fetch margin override data for the selected brand/vendor
  const fetchCategoryOverrides = useCallback(async () => {
    setIsFetchingData(true); // Set loading state for data fetch
    try {
      const res = await getMarginOverride(selectedItem?._id);
      if (res) {
        setCategoryOverrideData(res); // Update state with fetched data
      }
    } catch (error) {
      console.error("Failed to fetch margin overrides:", error);
      // Optionally show an error message to the user
    } finally {
      setIsFetchingData(false); // Clear loading state
    }
  }, [selectedItem?._id]); // Re-fetch when selectedItem ID changes

  // Effect hook to fetch data on component mount or when selectedItem changes
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component
    if (isMounted && selectedItem?._id) {
      fetchCategoryOverrides();
    }
    return () => {
      isMounted = false; // Cleanup: set flag to false when component unmounts
    };
  }, [selectedItem]); // Depend on selectedItem._id and memoized fetch function

  // Handler for updating the entire pricing strategy (all overrides)
  const handleUpdate = async () => {
    setIsUpdating(true); // Set loading state for update operation
    try {
      const res = await updatePricingStrategy({
        id: selectedItem?._id,
        isCustomPricing: true,
        override: categoryOverrideData, // Send the entire array of override data
      });

      if (setIsRender) setIsRender(!isRender);
    } catch (error) {
      console.error("Update failed:", error);
      // Optionally show an error message to the user
    } finally {
      setIsUpdating(false); // Clear loading state
    }
  };

  // Handler for input field changes within a specific category item
const handleInputChange = (e, index) => {
  const { name, value } = e.target;

  setCategoryOverrideData((prevData) => {
    const newData = [...prevData];
    const isEmpty = value === "";
    const isNegativeSignOnly = value === "-"; // 👈 Special case

    // Allow empty or just "-" for typing experience
    if (isEmpty) {
      newData[index][name] = "";
    } else if (isNegativeSignOnly) {
      newData[index][name] = value; // keep as string temporarily
    } else if (/^-?\d*\.?\d*$/.test(value)) {
      // Valid number format → convert to Number()
      newData[index][name] = value === "" ? "" : Number(value);
    } else {
      // Invalid input → keep previous value
      // or optionally show error
    }

    return newData;
  });
};
  // Handler for incrementing a specific category's margin percentage
const handleIncrement = (name, index) => {
  setCategoryOverrideData((prevData) => {
    const newData = [...prevData];
    newData[index] = {
      ...newData[index],
      [name]: (newData[index][name] || 0) + 1,
    };
    return newData;
  });
};

const handleDecrement = (name, index) => {
  setCategoryOverrideData((prevData) => {
    const newData = [...prevData];
    newData[index] = {
      ...newData[index],
      [name]: (newData[index][name] || 0) - 1, // ✅ Now allows negatives
    };
    return newData;
  });
};

  // Handler for the custom ON/OFF button (isCustom)
  const handleIsCustomToggle = (index) => {
    setCategoryOverrideData((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        isCustom: !newData[index].isCustom, // Toggle the boolean value
      };
      return newData;
    });
  };

  return (
    <Container
      fluid
      className="p-0"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Header Section: Brand Name Margins and "Using Category Margins" chip */}
      <Row className="d-flex justify-content-between align-items-center py-3 px-2 mx-0">
        <Col xs="auto">
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: "bold",
              fontSize: "18px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {selectedItem?.fullName}
          </Typography>
        </Col>
        <Col xs="auto" className="d-flex justify-content-end">
          <Chip
            label={
              selectedItem?.isCustomPricing
                ? "Using Category Margins"
                : "Using Default Margins"
            }
            icon={<CheckCircleIcon />}
            sx={{
              backgroundColor: !selectedItem?.isCustomPricing
                ? "#004a80"
                : "#440e62", // Dark purple
              borderRadius: "50px",
              color: "white",
              fontWeight: "bold",
              height: "40px",
              fontSize: "0.8rem",
              fontFamily: "Inter, sans-serif",
              "& .MuiChip-icon": {
                color: "white !important",
              },
            }}
          />
        </Col>
      </Row>

      {/* Blue Section Title: "Default Margins" */}
      <Row className="mx-0">
        <Col className="p-0 m-0">
          <div
            className="text-white text-center py-2"
            style={{ backgroundColor: "#004a80" }} // Custom blue background and rounded corners
          >
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: "bold", fontSize: "18px" }}
            >
              Default Margins
            </Typography>
          </div>
        </Col>
      </Row>

      {/* Margins Input Section: "Default Margins" label on left, Map and NonMap inputs on right */}
      <Row className="d-flex justify-content-center align-items-center mx-0 px-0">
        {/* Responsive column sizing */}
        <div className="d-flex align-items-center w-100">
          <Row className="d-flex align-items-center mx-0 px-0">
            <Col
              xs={4}
              style={{
                borderLeft: "0.5px solid #e1e5e8",
                borderRight: "0.5px solid #e1e5e8",
                borderBottom: "0.5px solid #e1e5e8",
                padding: "0.5rem  0.2rem",
                height: "70px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "normal",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Default Margins
              </Typography>
            </Col>
            <Col
              xs={3}
              style={{
                borderLeft: "0.5px solid #e1e5e8",
                borderRight: "0.5px solid #e1e5e8",
                borderBottom: "0.5px solid #e1e5e8",
                padding: "0.5rem  0.2rem",
                height: "70px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* Map Input Box */}
              <div className="input-wrapper">
                <label htmlFor="map" className="label">
                  Map
                </label>
                <input
                  type="number" // allows flexible typing like '1.', empty string, etc.
                  id="map"
                  name="mapPercentage"
                  // min="1"
                  value={formData?.mapPercentage ?? ""}
                  onChange={(e) => handleChange(e)}
                  
                />
                <div
                  style={{
                    position: "absolute", // Position buttons absolutely
                    right: "8px", // Align to the right side of the main container
                    top: "50%", // Vertically center
                    transform: "translateY(-50%)", // Adjust for exact vertical centering
                    display: "flex",
                    flexDirection: "column", // Stack buttons vertically
                    gap: "3px",
                  }}
                >
                  <button
                    onClick={() => handleGblIncrement("mapPercentage")}
                    style={{
                      backgroundColor: "#e5e5e6",
                      borderRadius: "6px",
                      border: "none",
                      padding: "0",
                      cursor: "pointer",
                      height: "20px",
                      width: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#666",
                      outline: "none",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </button>

                  <button
                    onClick={() => handleGblDecrement("mapPercentage")}
                    style={{
                      backgroundColor: "#e5e5e6",
                      borderRadius: "6px",
                      border: "none",
                      padding: "0",
                      cursor: "pointer",
                      height: "20px",
                      width: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#666",
                      outline: "none",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </Col>
            <Col
              xs={3}
              style={{
                borderLeft: "0.5px solid #e1e5e8",
                borderRight: "0.5px solid #e1e5e8",
                borderBottom: "0.5px solid #e1e5e8",
                padding: "0.5rem  0.2rem",
                height: "70px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* NonMap Input Box (structure identical to Map) */}
              <div className="input-wrapper">
                <label htmlFor="nonMap" className="label">
                  NonMap
                </label>
                <input
                  type="number"
                  id="nonMap"
                  name="nonMapPercentage" 
                  //  min="1"
                  value={formData?.nonMapPercentage ?? ""}
                  onChange={(e) => handleChange(e)}
                />

                {/* <input
                  type="number"
                  id="nonMap"
                  name="nonMap"
                  
                  value={formData?.nonMapPercentage ?? 0}
                  onChange={(e) => handleChange(e)}
                  onBlur={(e) => handleBlur(e)}
                /> */}
                <div
                  style={{
                    position: "absolute", // Position buttons absolutely
                    right: "8px", // Align to the right side of the main container
                    top: "50%", // Vertically center
                    transform: "translateY(-50%)", // Adjust for exact vertical centering
                    display: "flex",
                    flexDirection: "column", // Stack buttons vertically
                    gap: "3px",
                  }}
                >
                  <button
                    onClick={() => handleGblIncrement("nonMapPercentage")}
                    style={{
                      backgroundColor: "#e5e5e6",
                      borderRadius: "6px",
                      border: "none", // No border
                      padding: "0", // No padding
                      cursor: "pointer", // Pointer cursor on hover
                      height: "20px", // Fixed height
                      width: "20px", // Fixed width
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#666", // Dark grey color for the icon
                      outline: "none", // Remove outline on focus
                      transition: "background-color 0.2s", // Smooth transition for hover effect
                    }}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </button>

                  <button
                    onClick={() => handleGblDecrement("nonMapPercentage")}
                    style={{
                      backgroundColor: "#e5e5e6",
                      borderRadius: "6px",
                      border: "none",
                      padding: "0",
                      cursor: "pointer",
                      height: "20px",
                      width: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#666",
                      outline: "none",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </Col>
            <Col
              xs={1}
              style={{
                borderLeft: "0.5px solid #e1e5e8",
                borderRight: "0.5px solid #e1e5e8",
                borderBottom: "0.5px solid #e1e5e8",
                padding: "0.5rem  0.2rem",
                height: "70px",
                display: "flex",
                alignItems: "center",
              }}
            ></Col>
            <Col
              xs={1}
              style={{
                borderLeft: "0.5px solid #e1e5e8",
                borderRight: "0.5px solid #e1e5e8",
                borderBottom: "0.5px solid #e1e5e8",
                padding: "0.5rem  0.2rem",
                height: "70px",
                display: "flex",
                alignItems: "center",
              }}
            ></Col>
          </Row>
        </div>
      </Row>
      {/* Blue Section Title: "Category Margins" */}
      <Row className="mx-0 mb-3">
        {" "}
        {/* Added margin-bottom for spacing */}
        <Col style={{ padding: "0px" }}>
          <div
            className="text-white text-center py-2"
            style={{ backgroundColor: "#440e62" }} // Dark purple and rounded corners
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              Category Margins
            </Typography>
          </div>
        </Col>
      </Row>

      {/* Conditional Rendering for Loading or Data */}
      {isFetchingData ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Typography variant="h6" sx={{ fontFamily: "Inter, sans-serif" }}>
            Loading.....
          </Typography>
        </div>
      ) : (
        <div className="d-flex flex-column " style={{paddingBottom:"70px"}}>
          {categoryOverrideData?.map((item, index) => (
            <Row
              key={item._id || index} // Use _id if available, otherwise index as fallback
              className="d-flex justify-content-center align-items-center mx-0 my-2 p-0"
              style={{
                borderBottom:
                  index === categoryOverrideData.length - 1
                    ? "none"
                    : "0.5px solid #e1e5e8", // Border bottom for all but the last item
              }}
            >
              {/* Category Name Column */}
              <Col
                xs={4}
                style={{
                  borderLeft: "0.5px solid #e1e5e8",
                  borderRight: "0.5px solid #e1e5e8",
                  padding: "0.5rem 0.2rem",
                  height: "70px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column", // Stack text and ON/OFF button
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "normal",
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {item?.categoryName}
                </Typography>
                {/* Custom ON/OFF button */}
              </Col>

              {/* Map Input Column */}
              <Col
                xs={2}
                style={{
                  borderLeft: "0.5px solid #e1e5e8",
                  borderRight: "0.5px solid #e1e5e8",
                  padding: "0.5rem 0.2rem",
                  height: "70px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="input-wrapper">
                  <label htmlFor={`map-${index}`} className="label">
                    Map
                  </label>
                  <input
                    type="text"
                    id={`map-${index}`}
                    name="mapPercentage"
                    // min="1"
                    value={item?.mapPercentage} // Ensure value is initialized
                    onChange={(e) => handleInputChange(e, index)}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    <button
                      onClick={() => handleIncrement("mapPercentage", index)}
                      style={{
                        backgroundColor: "#e5e5e6", // Light grey background for buttons
                        borderRadius: "6px",
                        border: "none",
                        padding: "0",
                        cursor: "pointer",
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#666",
                        outline: "none",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </button>
                    <button
                      onClick={() => handleDecrement("mapPercentage", index)}
                      style={{
                        backgroundColor: "#e5e5e6",
                        borderRadius: "6px",
                        border: "none",
                        padding: "0",
                        cursor: "pointer",
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#666",
                        outline: "none",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </Col>

              {/* NonMap Input Column */}
              <Col
                xs={2}
                style={{
                  borderLeft: "0.5px solid #e1e5e8",
                  borderRight: "0.5px solid #e1e5e8",
                  padding: "0.5rem 0.2rem",
                  height: "70px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="input-wrapper">
                  <label htmlFor={`nonMap-${index}`} className="label">
                    NonMap
                  </label>
                  <input
                    type="text"
                    id={`nonMap-${index}`}
                    name="nonMapPercentage"
                    // min="1"
                    value={item?.nonMapPercentage} // Ensure value is initialized
                    onChange={(e) => handleInputChange(e, index)}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    <button
                      onClick={() => handleIncrement("nonMapPercentage", index)}
                      style={{
                        backgroundColor: "#e5e5e6",
                        borderRadius: "6px",
                        border: "none",
                        padding: "0",
                        cursor: "pointer",
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#666",
                        outline: "none",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </button>
                    <button
                      onClick={() => handleDecrement("nonMapPercentage", index)}
                      style={{
                        backgroundColor: "#e5e5e6",
                        borderRadius: "6px",
                        border: "none",
                        padding: "0",
                        cursor: "pointer",
                        height: "20px",
                        width: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#666",
                        outline: "none",
                        transition: "background-color 0.2s",
                      }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </Col>

              <Col
                xs={2}
                style={{
                  borderLeft: "0.5px solid #e1e5e8",
                  borderRight: "0.5px solid #e1e5e8",
                  padding: "0.5rem 0.2rem",
                  height: "70px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around", // Distribute space
                  cursor: "pointer", // Indicate clickable
                }}
              >
                <button
                  onClick={() => handleIsCustomToggle(index)}
                  style={{
                    backgroundColor: item?.isCustom ? "#17a858" : "#8c8c8c", // Dark purple for ON, grey for OFF
                    color: "white",
                    borderRadius: "50%", // Circular button
                    width: "45px",
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "bold",
                    marginTop: "5px", // Space between text and button
                    transition: "background-color 0.2s ease",
                  }}
                >
                  {item?.isCustom ? "ON" : "OFF"}
                </button>
              </Col>
              <Col
                xs={2}
                style={{
                  borderLeft: "0.5px solid #e1e5e8",
                  borderRight: "0.5px solid #e1e5e8",
                  padding: "0.5rem 0.2rem",
                  height: "70px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-around", // Distribute space
                  cursor: "pointer", // Indicate clickable
                }}
                onClick={() =>
                  navigate(
                    `/admin/products?brand=${item?.brandId}&categoryId=${
                      item?.categoryId
                    }&CategoryName=${
                      item?.categoryName
                    }&brandName=${selectedItem?.fullName
                      ?.split(" " && "&")
                      ?.join(" ")}`
                  )
                }
              >
                <div
                  style={{
                    backgroundColor: "#000",
                    borderRadius: "50%",
                    width: "45px",
                    height: "45px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src="/Arrow.png"
                    alt="arrow"
                    width={"15px"}
                    style={{ filter: "invert(1)" }}
                  />
                </div>
              </Col>
            </Row>
          ))}
        </div>
      )}

      {/* UpdateButton */}
      <hr />
      <Row
        className="mt-4 mx-0 px-0"
        style={{ maxWidth: "446px", backgroundColor: "#fff" }}
      >
        <Col className="d-flex justify-content-center m-0 p-0" xs={6}>
          <div
            style={{
              position: "fixed",
              bottom: "0px",
              //  left:"0px",
              maxWidth: "223px",
              margin: "0px auto",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              padding: "35px 0px",
              boxShadow: "0px",
              zIndex: "1401",
            }}
            className=" bg-white pl-3"
          >
            <button
              className=" btn w-100 text-white"
              onClick={() => handleGblUpdate(selectedItem?._id)}
              style={{
                backgroundColor: "#004a80",
                padding: "14px 5px",
                fontSize: "12px",
              }}
            >
              {isUpdating ? "Loading..." : `Save | Use Default Margins`}
            </button>
          </div>
        </Col>
        <Col className="d-flex justify-content-center m-0 p-0" xs={6}>
          <div
            style={{
              position: "fixed",
              bottom: "0px",
              // left:"0px",
              maxWidth: "223px",
              margin: "0px auto",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              padding: "35px 0px",
              boxShadow: "0px",
              zIndex: "1401",
            }}
            className=" bg-white px-3"
          >
            <button
              className=" btn w-100 text-white"
              onClick={handleUpdate}
              style={{
                backgroundColor: "#440e62",
                padding: "14px 5px",
                fontSize: "12px",
              }}
            >
              {isUpdating ? "Loading..." : `Save | Use Category Margins`}
            </button>
          </div>
        </Col>
      </Row>

      {/* Inline CSS (jsx style) */}
      <style jsx>{`
        .input-wrapper {
          position: relative;
          width: 100%;
          padding: 0px 5px;
        }
        input {
          border: 1px solid #000; /* Original border from your provided CSS */
          border-radius: 6px;
          width: 100%;
          padding: 15px 15px 15px 7px;
          font-size: 16px;
          margin: 0;
          -moz-appearance: textfield; /* Hide Firefox spinners */
          -webkit-appearance: none; /* Hide Chrome/Safari/Edge spinners */
        }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        .label {
          position: absolute;
          top: -0.8ex;
          left: 0.7em;
          background-color: #fafafa;
          color: #333;
          line-height: 0.9;
          font-size: 12px;
          z-index: 1;
        }
        /* Placeholder styling for consistency */
        ::placeholder {
          color: #7d7d7d;
          opacity: 1;
        }
        ::-ms-input-placeholder {
          color: #7d7d7d;
        }
      `}</style>
    </Container>
  );
};

export default ManageMarginOverride;
